import React from 'react';
import { cx } from './clases';

interface Props {
  /** Los botones (Guardar, Enviar…). En teléfono ocupan todo el ancho, en rejilla. */
  children: React.ReactNode;
  /** Renglón principal a la izquierda: «Para enviar faltan 2 colaboradores sin meta ni motivo». */
  mensaje?: React.ReactNode;
  /** Renglón pequeño bajo el mensaje: «Borrador guardado automáticamente hoy 10:42». */
  detalle?: React.ReactNode;
  /** `aviso` pone el ícono ámbar delante del mensaje (algo impide enviar). */
  tono?: 'normal' | 'aviso';
  /** Nombre de la región para el lector de pantalla. */
  etiqueta?: string;
  /**
   * Lo que dice la barra es de algo que se está reemplazando (la Captura carga
   * otra tienda): `aria-busy` y quien la usa la atenúa.
   */
  ocupada?: boolean;
  className?: string;
}

const IconoAviso = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="shrink-0 text-metas-aviso-icono"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6M12 16.5h.01" />
  </svg>
);

/**
 * Barra de acciones pegada al pie (`sticky`), como el `footer` del diseño:
 * mensaje a la izquierda y botones a la derecha; hasta 1023 px (teléfono y
 * tableta vertical), el mensaje arriba y los botones abajo, a todo lo ancho. Era
 * hasta 767: a 768, en fila, el mensaje se partía palabra por palabra y la barra
 * medía 351 px, un tercio de la pantalla (auditoría del 22-oct-2026).
 *
 * Es `sticky` y no `fixed`: se queda dentro de la columna de contenido del
 * panel (no tapa el menú lateral) y no necesita reservar espacio.
 */
export const BarraAccionesFija: React.FC<Props> = ({
  children,
  mensaje,
  detalle,
  tono = 'normal',
  etiqueta = 'Acciones',
  ocupada,
  className,
}) => (
  <div
    role="region"
    aria-label={etiqueta}
    aria-busy={ocupada || undefined}
    className={cx(
      'sticky bottom-0 z-10 flex flex-col gap-2.5 border-t border-metas-borde bg-metas-panel px-4 pb-5 pt-3 shadow-metas-barra',
      'lg:min-h-[76px] lg:flex-row lg:items-center lg:gap-4 lg:px-10 lg:py-3',
      className,
    )}
  >
    {(mensaje || detalle) && (
      <div className="flex min-w-0 items-center gap-2">
        {tono === 'aviso' && <IconoAviso />}
        <div className="flex min-w-0 flex-col gap-0.5">
          {mensaje && <span className="text-sm font-semibold text-metas-texto">{mensaje}</span>}
          {detalle && <span className="text-xs text-metas-texto-2">{detalle}</span>}
        </div>
      </div>
    )}
    <div className="hidden flex-grow lg:block" />
    <div className="grid auto-cols-fr grid-flow-col gap-2 lg:flex lg:items-center">{children}</div>
  </div>
);

export default BarraAccionesFija;
